import type { FormEventHandler, HTMLAttributes, ReactElement } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import cc from 'classcat'

import { CartContext } from './CartContext'
import { Tooltip } from '../../Tooltip'
import { addLineItemProductList, addPayment, sendButtonClick } from '../../../utils/googleAnalytics'
import { loadCart } from '../../../store/actions'
import { track } from '../../../utils/tracking'
import { useTranslate } from '../../../utils/translate'
import { withQueryAndScope } from '../../../urlGeneratorsPlain'
import addScript from '../../../utils/addScript'

function getTooltipContent(
  isThemePreview: boolean,
  hasInvalidCoupon: boolean,
  t: TranslateProps['t'],
): string | undefined {
  const translationPrefix = 'views.storefrontView.basketPageSection.checkoutLinkButton.disabledState.explanations.'
  if (!isThemePreview && !hasInvalidCoupon) return
  if (isThemePreview) {
    return t(`${translationPrefix}themePreview`)
  }
  return t(`${translationPrefix}couponConditionsNotMet`)
}

type CheckoutButtonProps = HTMLAttributes<HTMLButtonElement>

export function CheckoutButton(props: Readonly<CheckoutButtonProps>): ReactElement {
  const { className, ...htmlAttributes } = props

  const cart = useSelector<State, Core.Cart>((state) => state.get('cart').toJS())
  const [isButtonBusy, setIsButtonBusy] = useState(false)
  const isCartBusy = useSelector<State, boolean>((state) => state.getIn(['view', 'cart', 'isBusy'], false))
  const isThemePreview = useSelector<State, boolean>((state) => state.getIn(['view', 'isThemePreview']))

  const cartContext: any = useContext(CartContext)
  const isBeyond = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const isShopClosed = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'isClosed'])))
  const hasInvalidProducts = cart.productLineItems.some((item) => item.productId === null)
  const hasInvalidCoupon = cart.coupon ? cart.coupon.validationErrors.length > 0 : false
  const isMinimiumOrderPriceReached = cart.checkoutState ? cart.checkoutState.priceValidToOrder !== false : true
  const location = useLocation()
  const dispatch = useDispatch()
  const t = useTranslate('shop')

  const gotoCheckout = async () => {
    setIsButtonBusy(true)

    removeInvalidCoupons()
    // load actual cart for checking invalid products in parent views/cart.jsx
    const updatedCart = (await dispatch(loadCart())) as unknown as Core.Cart

    if (
      updatedCart.productLineItems?.length === 0 ||
      updatedCart.productLineItems?.some((item) => item.productId === null)
    )
      return

    if (isBeyond) {
      track('cart:checkout', { cart: updatedCart })

      if (window.parent !== window && isShopClosed) {
        if (confirm(t('views.storefrontView.basketPageSection.checkoutLinkButton.exitingIframe'))) {
          // switch from iFrame based to embedded preview since the former doesn't work with all payment methods
          const query = new URLSearchParams(location.search)
          query.set('preview', '1')
          window.parent.location.assign(
            withQueryAndScope(cart.checkoutUrl, {
              ...location,
              search: query.toString(),
            }) as string,
          )
        } else setIsButtonBusy(false)
      }

      // Stay in React land, but with a full reload for "security"
      else window.location.assign(withQueryAndScope(cart.checkoutUrl, location) as string)
    } else {
      // The story ends, you wake up in your epages6 checkout
      window.location.assign(cart.checkoutUrl)
    }
  }

  function removeInvalidCoupons() {
    if (cart.coupon?.lineItemPrice?.formatted === '-') {
      cartContext.removeCoupon()
    }
  }

  const tooltipContent = getTooltipContent(isThemePreview, hasInvalidCoupon, t)

  const button = (
    <button
      {...htmlAttributes}
      className={cc([
        className,
        {
          pending: isCartBusy || isButtonBusy,
        },
      ])}
      type="button"
      disabled={
        isThemePreview ||
        isCartBusy ||
        isButtonBusy ||
        hasInvalidCoupon ||
        !isMinimiumOrderPriceReached ||
        hasInvalidProducts
      }
      onClick={gotoCheckout}
    />
  )

  if (!tooltipContent) {
    return button
  }

  return <Tooltip trigger={button} content={tooltipContent} side="top" hideIcon sideOffset={-30} />
}

type PaypalButtonProps = {
  color?: 'gold' | 'blue' | 'silver' | 'white' | 'black'
  shape?: 'pill' | 'rect'
  size?: string
} & CheckoutButtonProps

export function PaypalButton(props: Readonly<PaypalButtonProps>): ReactElement | null {
  const { shape = 'pill', color = 'blue', size = '26', className } = props
  const cart = useSelector<State, Core.Cart>((state) => state.get('cart').toJS())
  const isCartBusy = useSelector<State, boolean>((state) => state.getIn(['view', 'cart', 'isBusy'], false))
  // Check if a preview theme is set.
  // We have to check for an attribute of `themePreview`, because
  // `themePreview` itself is always found on the server.
  const isThemePreview = useSelector<State, boolean>((state) => state.hasIn(['view', 'themePreview', 'theme']))

  const buttonUrl = `https://www.paypalobjects.com/webstatic/en_US/i/btn/png/${color}-${shape}-paypalcheckout-${size}px.png`
  const paypalFormRef = useRef<HTMLFormElement>(null)
  const hasInvalidCoupon = cart.coupon ? cart.coupon.validationErrors.length > 0 : false

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    const hitCallback = () => paypalFormRef.current?.submit()

    addLineItemProductList(cart.productLineItems)
    addPayment('PayPal Express', hitCallback)
  }

  const buttonData = cart.checkoutButtons.find((button) => button.name === 'paypalexpress')

  if (!buttonData || cart.grandTotal.amount === 0) return null

  const classNames = cc([
    className,
    {
      'checkout-pending': isCartBusy,
      'is-hidden': isThemePreview || hasInvalidCoupon,
    },
  ])

  return (
    <form method="post" action={cart.checkoutUrl} className={classNames} ref={paypalFormRef} onSubmit={onSubmit}>
      <input type="hidden" name="PaymentMethodID" value={buttonData.paymentMethodId} />
      <input type="hidden" name="ChangeAction" value="PickupBasket" />
      <input type="hidden" name="PickupToken" value={cart.pickupToken || undefined} />
      <input type="hidden" name="ChangeAction" value="BlockMonitor" />
      <input type="hidden" name="ChangeAction" value="PayPalExpress" />
      <button
        type="submit"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
        onClick={() => sendButtonClick('payment', 'PayPal Express')}
      >
        <img src={buttonUrl} alt="PayPal Checkout" />
      </button>
    </form>
  )
}

// Add amazon properties to window.
declare global {
  interface Window {
    OffAmazonPayments?: any
    onAmazonPaymentsReady: VoidFunction
  }
}
type AmazonButtonProps = CheckoutButtonProps
export function AmazonButton({ className }: Readonly<AmazonButtonProps>): ReactElement | null {
  const cart = useSelector<State, Core.Cart>((state) => state.get('cart').toJS())
  const isCartBusy = useSelector<State, boolean>((state) => state.getIn(['view', 'cart', 'isBusy'], false))
  // Check if a preview theme is set.
  // We have to check for an attribute of `themePreview`, because
  // `themePreview` itself is always found on the server.
  const isThemePreview = useSelector<State, boolean>((state) => state.hasIn(['view', 'themePreview', 'theme']))

  const amazonFormRef = useRef<HTMLFormElement>(null)
  const hasInvalidCoupon = cart.coupon ? cart.coupon.validationErrors.length > 0 : false
  const buttonData = cart.checkoutButtons.find((button) => button.name === 'amazonpayments')

  useEffect(() => {
    const buttonData = cart.checkoutButtons.find((button) => button.name === 'amazonpayments')

    // do not do anything if we do not have an amazon button
    if (buttonData) {
      // if the amazon script was not loaded yet create the button when it is ready and add the script tag to the head
      if (!window.OffAmazonPayments) {
        window.onAmazonPaymentsReady = () => createAmazonPaymentsButton(buttonData)
        addScript(buttonData.widgetUrl)
      } else {
        // if the amazon script is already loaded just create the button
        createAmazonPaymentsButton(buttonData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!buttonData) return null

  /**
   * Triggers the checkout with Amazon Pay
   *
   * @param {Object} orderReference An Amazon object of the order
   */
  const checkoutWithAmazon = (orderReference: any) => {
    if (amazonFormRef.current) {
      amazonFormRef.current.AmazonRefId.value = orderReference.getAmazonOrderReferenceId()
      amazonFormRef.current.submit()
    }
  }

  /**
   * Creates the Amazon Pay button
   *
   * @param   {Object}    buttonData  Data for the Amazon Pay button
   * @returns {Button}                The Amazon Pay button
   */
  const createAmazonPaymentsButton = (buttonData) => {
    return new window.OffAmazonPayments.Widgets.Button({
      sellerId: buttonData.dataMerchantId,
      onSignIn: (orderReference: any) => checkoutWithAmazon(orderReference),
    }).bind('PayWithAmazon')
  }

  const classNames = cc([
    className,
    {
      'checkout-pending': isCartBusy,
      'is-hidden': isThemePreview || hasInvalidCoupon,
    },
  ])

  return (
    <div className={classNames}>
      <div id="PayWithAmazon" onClick={() => sendButtonClick('payment', 'Amazon Pay')}>
        <img src={buttonData.image} alt="Amazon Pay Checkout" style={{ cursor: 'pointer' }} />
      </div>
      <form method="post" action={cart.checkoutUrl} ref={amazonFormRef}>
        <input type="hidden" name="AmazonRefId" value="" />
        <input type="hidden" name="ViewAction" value="ViewMultiCheckoutAmazonAddress" />
        <input type="hidden" name="UpdateViewAction" value="ViewMultiCheckoutAmazonAddress" />
        <input type="hidden" name="ChangeAction" value="PayWithAmazonPayments" />
      </form>
    </div>
  )
}

export function PayPalCommerceButton(): ReactElement | null {
  const cart = useSelector<State, Core.Cart>((state) => state.get('cart').toJS())
  const buttonData = cart.checkoutButtons.find((button) => button.name === 'ppcppayment')

  if (!buttonData || cart.grandTotal.amount === 0) return null

  return <div id="paypal-button-container"></div>
}
